import { styled, alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { Box } from '@mui/material';
import { spacing, colors } from '../../../styles/theme';

// Container principal de estadísticas
export const StatsContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderRadius: spacing.lg,
  boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)',
  border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
  padding: spacing.lg,
  marginBottom: spacing.xl,
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0,
}));

// Item individual de estadística
export const StatItem = styled(Box)(({ theme }) => ({
  flex: 1,
  minWidth: '150px',
  display: 'flex',
  alignItems: 'center',
  padding: spacing.sm,
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderRight: 'none',
  },
  '&[data-emphasis="true"]': {
    flex: '1.65 1 0',
    minWidth: '220px',
    padding: theme.spacing(2, 2.75),
  },
  '&[data-compact="true"]': {
    flex: '0.82 1 0',
    minWidth: '112px',
    padding: theme.spacing(0.5, 1),
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '100%',
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
    '&[data-emphasis="true"]': {
      minWidth: '100%',
    },
    '&[data-compact="true"]': {
      minWidth: '100%',
    },
  },
}));

// Container del icono
export const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'iconSize',
})<{ iconSize?: 'sm' | 'md' | 'lg' }>(({ theme, iconSize = 'md' }) => ({
  width: iconSize === 'sm' ? 40 : iconSize === 'lg' ? 52 : 48,
  height: iconSize === 'sm' ? 40 : iconSize === 'lg' ? 52 : 48,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight:
    iconSize === 'sm' ? theme.spacing(1) : iconSize === 'lg' ? theme.spacing(2) : spacing.md,
  transition: 'all 0.2s ease-in-out',
  '& svg': {
    fontSize: iconSize === 'sm' ? '1.35rem' : iconSize === 'lg' ? '1.65rem' : '1.5rem',
  },
  '&:hover': {
    transform: 'scale(1.05)',
  },
}));

// Container de información de la estadística
export const StatInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.xs,
}));

// Texto de etiqueta
export const StatLabel = styled(Box)(({ theme }) => ({
  fontSize: '14px',
  color: theme.palette.text.secondary,
  fontWeight: 500,
}));

// Texto del valor
export const StatValue = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'valueSize',
})<{ valueSize?: 'sm' | 'md' | 'lg' }>(({ theme, valueSize = 'md' }) => ({
  fontSize: valueSize === 'sm' ? '1.125rem' : valueSize === 'lg' ? '1.5rem' : '24px',
  fontWeight: 700,
  color: theme.palette.text.primary,
  lineHeight: 1.2,
}));

/** Variantes de iconos según el tema activo (claro u oscuro) */
export function getInventoryStatsIconSx(theme: Theme) {
  return {
    inventory: {
      backgroundColor: alpha(theme.palette.info.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
      color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.main,
    },
    sales: {
      backgroundColor: alpha(
        theme.palette.success.main,
        theme.palette.mode === 'dark' ? 0.22 : 0.12,
      ),
      color:
        theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.main,
    },
    cost: {
      backgroundColor: alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.12),
      color: theme.palette.mode === 'dark' ? theme.palette.error.light : theme.palette.error.main,
    },
    unassigned: {
      backgroundColor: alpha('#7c3aed', theme.palette.mode === 'dark' ? 0.28 : 0.12),
      color: theme.palette.mode === 'dark' ? '#c4b5fd' : '#7c3aed',
    },
    assigned: {
      backgroundColor: alpha('#4f46e5', theme.palette.mode === 'dark' ? 0.28 : 0.12),
      color: theme.palette.mode === 'dark' ? '#a5b4fc' : '#4f46e5',
    },
    inTransit: {
      backgroundColor: alpha('#0d9488', theme.palette.mode === 'dark' ? 0.28 : 0.12),
      color: theme.palette.mode === 'dark' ? '#5eead4' : '#0f766e',
    },
    availability: {
      backgroundColor: theme.palette.action.hover,
      color: theme.palette.text.secondary,
    },
    warning: {
      backgroundColor: alpha(
        theme.palette.warning.main,
        theme.palette.mode === 'dark' ? 0.22 : 0.12,
      ),
      color:
        theme.palette.mode === 'dark' ? theme.palette.warning.light : theme.palette.warning.main,
    },
  } as const;
}

// Container para estadísticas de disponibilidad
export const AvailabilityStatsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 0,
}));

// Item de estadística de disponibilidad (sucursal: más columnas, layout más compacto)
export const AvailabilityStatItem = styled(Box)(({ theme }) => ({
  flex: '0.88 1 0',
  minWidth: '108px',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0.5, 0.875),
  borderRight: `1px solid ${theme.palette.divider}`,
  '&:last-child': {
    borderRight: 'none',
  },
  [theme.breakpoints.down('md')]: {
    minWidth: '100%',
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
}));

// Container para información de margen
export const MarginInfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: spacing.xs,
  marginTop: spacing.sm,
}));

// Texto de margen
export const MarginText = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isPositive',
})<{ isPositive: boolean }>(({ theme, isPositive }) => ({
  fontSize: '12px',
  color: isPositive ? theme.palette.success.main : theme.palette.error.main,
  fontWeight: 600,
}));

/**
 * Variantes de icono para tema claro (p. ej. OrdersSummaryBar).
 * En componentes con modo oscuro usar `getInventoryStatsIconSx(theme)`.
 */
export const IconVariants = {
  inventory: {
    backgroundColor: colors.info.light + '20',
    color: colors.info.main,
  },
  sales: {
    backgroundColor: colors.success.light + '20',
    color: colors.success.main,
  },
  cost: {
    backgroundColor: colors.error.light + '20',
    color: colors.error.main,
  },
  unassigned: {
    backgroundColor: '#7c3aed20',
    color: '#7c3aed',
  },
  assigned: {
    backgroundColor: '#4f46e520',
    color: '#4f46e5',
  },
  availability: {
    backgroundColor: colors.grey[100],
    color: colors.grey[600],
  },
} as const;
